import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Star, Filter, Send, UserPlus, Mail, MessageSquare, History, Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientReviews({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [respondingIdx, setRespondingIdx] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('reviews'); // reviews | request | history
  const [requestForm, setRequestForm] = useState({ customer_name: '', phone: '', email: '', platform: 'google' });
  const [requestSending, setRequestSending] = useState(false);
  const [requestHistory, setRequestHistory] = useState([]);
  const [reviewLinks, setReviewLinks] = useState({});
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCustomers, setBulkCustomers] = useState([{ name: '', phone: '', email: '' }]);

  const fetchReviews = useCallback(() => {
    fetch(`${API}/api/client/dashboard/reviews`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const fetchRequestHistory = useCallback(() => {
    fetch(`${API}/api/client/dashboard/reviews/requests`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setRequestHistory(d.requests || []))
      .catch(console.error);
  }, [getHeaders]);

  const fetchReviewLinks = useCallback(() => {
    fetch(`${API}/api/client/dashboard/reviews/links`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setReviewLinks(d))
      .catch(console.error);
  }, [getHeaders]);

  useEffect(() => { fetchReviews(); fetchReviewLinks(); }, [fetchReviews, fetchReviewLinks]);

  const handleRespond = async (idx) => {
    if (!responseText.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/reviews/${idx}/respond`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: responseText }),
      });
      if (res.ok) { setRespondingIdx(null); setResponseText(''); fetchReviews(); }
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.phone && !requestForm.email) return;
    setRequestSending(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/reviews/request`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm),
      });
      const d = await res.json();
      if (res.ok) {
        setRequestForm({ customer_name: '', phone: '', email: '', platform: 'google' });
        fetchRequestHistory();
        setTab('history');
      } else {
        alert(d.detail || t('reviews.failedSend'));
      }
    } catch (e) { console.error(e); }
    finally { setRequestSending(false); }
  };

  const handleBulkSend = async () => {
    const valid = bulkCustomers.filter(c => c.phone || c.email);
    if (!valid.length) return;
    setRequestSending(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/reviews/request-bulk`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ customers: valid, platform: requestForm.platform, channels: ['sms', 'email'] }),
      });
      const d = await res.json();
      if (res.ok) {
        setBulkCustomers([{ name: '', phone: '', email: '' }]);
        setBulkMode(false);
        fetchRequestHistory();
        setTab('history');
        alert(t('reviews.sentNoTotal', { sent: d.sent, total: d.total }));
      } else {
        alert(d.detail || t('reviews.bulkFailed'));
      }
    } catch (e) { console.error(e); }
    finally { setRequestSending(false); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('reviews.loading')}</div>;
  if (!data) return <div className="text-red-400 text-center py-12">{t('reviews.loadFailed')}</div>;

  const filtered = sourceFilter === 'all' ? data.reviews : data.reviews.filter(r => r.source === sourceFilter);
  const avgAll = data.sources.length > 0 ? (data.sources.reduce((s, r) => s + r.avg_rating * r.count, 0) / data.total).toFixed(1) : '0.0';

  const hasReviewLinks = Object.values(reviewLinks).some(v => v);

  return (
    <div className="space-y-6" data-testid="reviews-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('reviews.overallRating')}</p>
            <p className="text-4xl font-bold text-gray-900 mt-1">{avgAll}</p>
            <div className="flex justify-center gap-0.5 mt-2">
              {[1,2,3,4,5].map(s => <Star key={s} className={`h-4 w-4 ${s <= Math.round(avgAll) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('reviews.totalReviews')}</p>
            <p className="text-4xl font-bold text-gray-900 mt-1">{data.total}</p>
            <p className="text-xs text-gray-400 mt-1">{t('reviews.acrossPlatforms', { count: data.sources.length })}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('reviews.responseRate')}</p>
            <p className="text-4xl font-bold text-gray-900 mt-1">
              {data.total > 0 ? Math.round(data.reviews.filter(r => r.response).length / data.total * 100) : 0}%
            </p>
            <p className="text-xs text-gray-400 mt-1">{t('reviews.respondedCount', { count: data.reviews.filter(r => r.response).length })}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-50 rounded-lg p-1" data-testid="reviews-tab-nav">
        {[
          { id: 'reviews', label: t('reviews.subtab.reviews'), Icon: Star },
          { id: 'request', label: t('reviews.subtab.request'), Icon: UserPlus },
          { id: 'history', label: t('reviews.subtab.history'), Icon: History },
        ].map(tb => (
          <button
            key={tb.id}
            onClick={() => { setTab(tb.id); if (tb.id === 'history') fetchRequestHistory(); }}
            data-testid={`reviews-subtab-${tb.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${tab === tb.id ? 'bg-amber-600/20 text-amber-400' : 'text-gray-400 hover:text-gray-900'}`}
          >
            <tb.Icon className="h-3.5 w-3.5" /> {tb.label}
          </button>
        ))}
      </div>

      {/* Reviews Tab */}
      {tab === 'reviews' && (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <Filter className="h-4 w-4 text-gray-500" />
            <button onClick={() => setSourceFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs transition-all ${sourceFilter === 'all' ? 'bg-blue-600 text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-900'}`}>
              {t('reviews.all')} ({data.total})
            </button>
            {data.sources.map(s => (
              <button key={s.name} onClick={() => setSourceFilter(sourceFilter === s.name ? 'all' : s.name)}
                className={`px-3 py-1.5 rounded-lg text-xs transition-all ${sourceFilter === s.name ? 'bg-blue-600 text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-900'}`}>
                {s.name} ({s.count}) - {s.avg_rating}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {filtered.map((review, i) => (
              <Card key={i} className="bg-white border-gray-200">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 text-sm">{review.reviewer_name}</span>
                        <Badge className="bg-gray-800 text-gray-400 text-[10px]">{review.source}</Badge>
                        <span className="text-[10px] text-gray-500">{review.date ? new Date(review.date).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="flex gap-0.5 mt-1">
                        {[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />)}
                      </div>
                      <p className="text-sm text-gray-300 mt-2">{review.text}</p>
                      {review.response ? (
                        <div className="mt-3 pl-4 border-l-2 border-blue-600/40">
                          <p className="text-xs text-gray-500">{t('reviews.businessResponse')}</p>
                          <p className="text-sm text-gray-400 mt-0.5">{review.response}</p>
                        </div>
                      ) : respondingIdx === i ? (
                        <div className="mt-3 space-y-2">
                          <textarea value={responseText} onChange={e => setResponseText(e.target.value)} placeholder={t('reviews.writeResponse')}
                            className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm resize-none h-20 focus:border-blue-500 focus:outline-none"
                            data-testid={`review-response-input-${i}`} />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleRespond(i)} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`review-respond-submit-${i}`}>
                              <Send className="h-3 w-3 mr-1" /> {sending ? t('reviews.sending') : t('reviews.sendResponse')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRespondingIdx(null); setResponseText(''); }} className="text-gray-400 text-xs">{t('reviews.cancel')}</Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setRespondingIdx(i)} className="mt-2 text-blue-400 hover:text-blue-300 text-xs p-0 h-auto" data-testid={`review-respond-btn-${i}`}>
                          {t('reviews.replyToReview')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && <p className="text-gray-500 text-center py-8">{t('reviews.noReviewsFound')}</p>}
          </div>
        </>
      )}

      {/* Request Review Tab */}
      {tab === 'request' && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-6">
            {!hasReviewLinks ? (
              <div className="text-center py-8">
                <p className="text-gray-400 text-sm">{t('reviews.noLinksTitle')}</p>
                <p className="text-gray-500 text-xs mt-2">{t('reviews.noLinksHint')}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-900 font-medium text-sm">{bulkMode ? t('reviews.bulkRequest') : t('reviews.singleRequest')}</h3>
                  <Button size="sm" variant="ghost" onClick={() => setBulkMode(!bulkMode)} className="text-xs text-gray-400" data-testid="toggle-bulk-mode">
                    {bulkMode ? t('reviews.singleSend') : t('reviews.bulkSend')}
                  </Button>
                </div>

                {/* Platform selector */}
                <div className="flex gap-2 mb-4">
                  {[
                    { id: 'google', label: 'Google', color: 'bg-red-600/20 text-red-400' },
                    { id: 'facebook', label: 'Facebook', color: 'bg-blue-600/20 text-blue-400' },
                    { id: 'yelp', label: 'Yelp', color: 'bg-orange-600/20 text-orange-400' },
                  ].filter(p => reviewLinks[p.id]).map(p => (
                    <button key={p.id} onClick={() => setRequestForm(f => ({ ...f, platform: p.id }))}
                      data-testid={`review-platform-${p.id}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${requestForm.platform === p.id ? p.color + ' ring-1 ring-current' : 'bg-gray-800 text-gray-400'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>

                {!bulkMode ? (
                  /* Single Send Form */
                  <form onSubmit={handleSendRequest} className="space-y-3" data-testid="review-request-form">
                    <input type="text" placeholder={t('reviews.customerName')} value={requestForm.customer_name}
                      onChange={e => setRequestForm(f => ({ ...f, customer_name: e.target.value }))}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                      data-testid="review-request-name" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                        <input type="tel" placeholder={t('reviews.phoneSms')} value={requestForm.phone}
                          onChange={e => setRequestForm(f => ({ ...f, phone: e.target.value }))}
                          className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                          data-testid="review-request-phone" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                        <input type="email" placeholder={t('reviews.email')} value={requestForm.email}
                          onChange={e => setRequestForm(f => ({ ...f, email: e.target.value }))}
                          className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
                          data-testid="review-request-email" />
                      </div>
                    </div>
                    <Button type="submit" disabled={requestSending || (!requestForm.phone && !requestForm.email)}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="review-request-send-btn">
                      <Send className="h-4 w-4 mr-2" /> {requestSending ? t('reviews.sending') : t('reviews.sendRequest')}
                    </Button>
                  </form>
                ) : (
                  /* Bulk Send */
                  <div className="space-y-3" data-testid="review-bulk-form">
                    {bulkCustomers.map((c, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input type="text" placeholder={t('reviews.name')} value={c.name}
                          onChange={e => { const u = [...bulkCustomers]; u[i].name = e.target.value; setBulkCustomers(u); }}
                          className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none" />
                        <input type="tel" placeholder={t('reviews.phone')} value={c.phone}
                          onChange={e => { const u = [...bulkCustomers]; u[i].phone = e.target.value; setBulkCustomers(u); }}
                          className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none" />
                        <input type="email" placeholder={t('reviews.email')} value={c.email}
                          onChange={e => { const u = [...bulkCustomers]; u[i].email = e.target.value; setBulkCustomers(u); }}
                          className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none" />
                        {bulkCustomers.length > 1 && (
                          <button onClick={() => setBulkCustomers(bulkCustomers.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setBulkCustomers([...bulkCustomers, { name: '', phone: '', email: '' }])}
                        className="text-xs text-gray-400" data-testid="review-bulk-add-row">
                        <Plus className="h-3 w-3 mr-1" /> {t('reviews.addRow')}
                      </Button>
                    </div>
                    <Button onClick={handleBulkSend} disabled={requestSending || !bulkCustomers.some(c => c.phone || c.email)}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="review-bulk-send-btn">
                      <Send className="h-4 w-4 mr-2" /> {requestSending ? t('reviews.sending') : t('reviews.sendToN', { count: bulkCustomers.filter(c => c.phone || c.email).length })}
                    </Button>
                  </div>
                )}

                <p className="text-gray-500 text-xs mt-3">
                  {t('reviews.footnote', { platform: requestForm.platform.charAt(0).toUpperCase() + requestForm.platform.slice(1) })}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Request History Tab */}
      {tab === 'history' && (
        <div className="space-y-3" data-testid="review-request-history">
          {requestHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('reviews.noHistory')}</p>
          ) : requestHistory.map((req, i) => (
            <Card key={i} className="bg-white border-gray-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-gray-900 text-sm font-medium">{req.customer_name || t('reviews.unknown')}</span>
                    <div className="flex gap-2 mt-1">
                      {req.customer_phone && <span className="text-xs text-gray-400">{req.customer_phone}</span>}
                      {req.customer_email && <span className="text-xs text-gray-400">{req.customer_email}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={req.status === 'sent' ? 'bg-green-50 text-green-400 text-[10px]' : 'bg-red-50 text-red-400 text-[10px]'}>
                      {req.status}
                    </Badge>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {req.platform} {req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}
                    </p>
                    <div className="flex gap-1 mt-1 justify-end">
                      {req.results?.sms?.success && <Badge className="bg-green-900/20 text-green-400 text-[9px]">{t('reviews.smsSent')}</Badge>}
                      {req.results?.email?.success && <Badge className="bg-blue-50 text-blue-400 text-[9px]">{t('reviews.emailSent')}</Badge>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
